/**
 * TopNavigation Component
 *
 * Main navigation bar for the dashboard.
 * Includes logo, navigation links, search, notifications, and user menu.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@/config/routes';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/hooks/shared/useLanguage';
import { SearchBar } from './SearchBar';
import type { PersonNodeCanvas } from '@/types/tree-dashboard.types';

interface TopNavigationProps {
  activeTab?: 'tree' | 'records' | 'dna' | 'photos';
  persons?: PersonNodeCanvas[]; // For search functionality
  onSelectPerson?: (personId: string) => void; // Callback when person is selected from search
}

export function TopNavigation({ activeTab = 'tree', persons = [], onSelectPerson }: TopNavigationProps) {
  const { user, signOut } = useAuth();
  const { t } = useTranslation(['trees', 'common']);
  const { currentLanguage, changeLanguage } = useLanguage();
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  const navigationItems = [
    { key: 'tree' as const, label: t('trees:navigation.myTree'), href: ROUTES.DASHBOARD },
    // Temporarily hidden - will be activated soon
    // { key: 'records' as const, label: t('trees:navigation.records'), href: '#' },
    // { key: 'dna' as const, label: t('trees:navigation.dna'), href: '#' },
    // { key: 'photos' as const, label: t('trees:navigation.photos'), href: '#' },
  ];

  return (
    <header className="flex items-center justify-between h-14 md:h-16 border-b border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-background/90 backdrop-blur-md px-4 md:px-6 z-50 shadow-sm">
      {/* Left: Logo and Navigation */}
      <div className="flex items-center gap-4 md:gap-6 lg:gap-8">
        {/* Logo */}
        <Link to={ROUTES.DASHBOARD} className="flex items-center gap-2 lg:gap-3 text-primary">
          <div className="size-7 md:size-8 bg-primary rounded-lg flex items-center justify-center text-white flex-shrink-0">
            <span className="material-symbols-outlined text-lg md:text-xl">account_tree</span>
          </div>
          <h2 className="text-slate-900 dark:text-white text-sm md:text-base lg:text-lg font-bold leading-tight tracking-tight">
            {t('trees:navigation.appName')}
          </h2>
        </Link>

        {/* Navigation Links */}
        <nav className="hidden lg:flex items-center gap-6">
          {navigationItems.map((item) => (
            <Link
              key={item.key}
              to={item.href}
              className={`
                text-sm font-semibold transition-colors py-5
                ${
                  activeTab === item.key
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-slate-600 dark:text-slate-400 hover:text-primary'
                }
              `}
            >
              {item.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Right: Search, Language, User */}
      <div className="flex items-center gap-2 md:gap-3 lg:gap-4">
        {/* Mobile/Tablet Search Icon - Shows on screens < 1024px */}
        {onSelectPerson && (
          <button
            onClick={() => setIsMobileSearchOpen(true)}
            className="lg:hidden size-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex-shrink-0"
            aria-label={t('common:dashboard.search.placeholder')}
            title={t('common:dashboard.search.placeholder')}
          >
            <span className="material-symbols-outlined text-xl text-slate-600 dark:text-slate-400">search</span>
          </button>
        )}

        {/* Desktop Search - Hidden on mobile/tablet, visible on desktop */}
        {onSelectPerson && (
          <SearchBar
            persons={persons}
            onSelectPerson={onSelectPerson}
            className="hidden lg:block"
          />
        )}

        {/* Language Switcher - Compact on mobile */}
        <div className="flex items-center gap-1 sm:gap-1.5 lg:gap-2 border border-primary/20 rounded-full p-0.5 lg:p-1">
          <button
            onClick={() => changeLanguage('en')}
            className={`px-2 sm:px-2.5 lg:px-3 py-0.5 lg:py-1 text-[10px] sm:text-xs font-semibold rounded-full transition-colors ${
              currentLanguage === 'en'
                ? 'bg-primary text-white'
                : 'text-slate-600 dark:text-slate-400 hover:text-primary'
            }`}
            aria-label="Switch to English"
            title="English"
          >
            <span className="hidden sm:inline">EN</span>
            <span className="sm:hidden">E</span>
          </button>
          <button
            onClick={() => changeLanguage('ar')}
            className={`px-2 sm:px-2.5 lg:px-3 py-0.5 lg:py-1 text-[10px] sm:text-xs font-semibold rounded-full transition-colors ${
              currentLanguage === 'ar'
                ? 'bg-primary text-white'
                : 'text-slate-600 dark:text-slate-400 hover:text-primary'
            }`}
            aria-label="Switch to Arabic"
            title="العربية"
          >
            ع
          </button>
        </div>

        {/* Divider - Hidden on small screens */}
        <div className="hidden sm:block h-6 lg:h-8 w-px bg-slate-200 dark:border-slate-800"></div>

        {/* User Profile - Compact on mobile */}
        <div className="relative group">
          <button className="bg-primary/10 border border-primary/20 rounded-full p-0.5 sm:p-0.5 lg:p-1 flex items-center gap-1.5 lg:gap-2 pr-1 sm:pr-2 lg:pr-3 hover:bg-primary/20 transition-colors">
            <div className="size-6 sm:size-7 lg:size-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-xs sm:text-sm">
              {user?.email?.charAt(0).toUpperCase() || 'U'}
            </div>
            <span className="text-[10px] lg:text-xs font-bold text-primary hidden sm:inline">{t('common:dashboard.userMenu.pro')}</span>
          </button>

          {/* Dropdown Menu - Responsive positioning */}
          <div className="absolute right-0 top-full mt-1 lg:mt-2 w-40 sm:w-44 lg:w-48 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
            <div className="p-1.5 lg:p-2">
              <div className="px-2 lg:px-3 py-1.5 lg:py-2 text-[10px] lg:text-xs text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800 truncate">
                {user?.email}
              </div>
              <button
                onClick={() => signOut()}
                className="w-full text-left px-2 lg:px-3 py-1.5 lg:py-2 text-xs lg:text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors mt-0.5 lg:mt-1"
              >
                {t('trees:userMenu.logout')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile/Tablet Search Modal - Shows on screens < 1024px with highest z-index */}
      {isMobileSearchOpen && onSelectPerson && (
        <>
          {/* Backdrop - blocks all content except sidebar */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[190] lg:hidden"
            onClick={() => setIsMobileSearchOpen(false)}
            aria-hidden="true"
          />

          {/* Search Modal Content */}
          <div className="fixed inset-0 z-[200] flex items-start justify-center lg:hidden pointer-events-none">
            <div className="w-full max-w-2xl bg-white dark:bg-slate-900 rounded-b-2xl shadow-2xl animate-in slide-in-from-top duration-300 pointer-events-auto">
              {/* Search Header */}
              <div className="flex items-center gap-3 p-4 border-b border-slate-200 dark:border-slate-800">
                <button
                  onClick={() => setIsMobileSearchOpen(false)}
                  className="size-10 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors flex-shrink-0"
                  aria-label="Close search"
                >
                  <span className="material-symbols-outlined text-xl text-slate-600 dark:text-slate-400">arrow_back</span>
                </button>
                <SearchBar
                  persons={persons}
                  onSelectPerson={(personId) => {
                    onSelectPerson(personId);
                    setIsMobileSearchOpen(false);
                  }}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
}
